/* ============================================================
   HeadsetPlus — "Search in this collection"

   A text box in the filter sidebar that narrows the collection without a page
   load. Two layers, because Shopify collection URLs do not accept a `q`
   parameter and there is no server-side in-collection text search to call:

     1. INSTANT — hide the rendered .hp-product-card elements that do not match.
        No network, no reflow of anything else, and matching cards keep their
        Quick View / Add to Cart. About 3 of every 4 collections fit on a single
        page, so for those this already searches the entire collection.

     2. INDEX — when the collection spans several pages, lazily fetch
        ?view=searchdata (templates/collection.searchdata.liquid) on the first
        keystroke, cache it in sessionStorage, and list the matches that live on
        other pages beneath the grid. ~30KB per 50 products, versus roughly 5MB
        to prefetch the rendered pages.

   Ships as its own asset rather than being folded into hp-filters.js, which
   also loads on /search where an in-collection box means nothing.
   ============================================================ */
(function () {
  'use strict';

  var ROOT       = '[data-hp-colsearch-root]';
  var INPUT      = '[data-hp-colsearch]';
  var CARD       = '.hp-product-card[data-hp-search]';
  var NOMATCH    = 'hp-card--nomatch';
  var DEBOUNCE   = 120;
  var GA_IDLE    = 700;
  var MAX_PAGES  = 20;   /* 20 x 50 = 1,000 products. Beyond that, stay page-scoped. */
  var MIN_INDEX_CHARS = 2; /* below this, a query matches most of the catalogue anyway */

  var timer   = null;
  var gaTimer = null;
  var gaSent  = '';
  var query   = '';

  /* Per-collection index state. null = never fetched, [] = fetched and empty. */
  var index        = null;
  var indexPending = false;
  var indexFailed  = false;

  function roots()  { return Array.prototype.slice.call(document.querySelectorAll(ROOT)); }
  function first()  { return document.querySelector(ROOT); }
  function grid()   { return document.querySelector('.hp-grid:not([data-hp-colsearch-more-grid])'); }
  function cards()  { return Array.prototype.slice.call(document.querySelectorAll(CARD)); }

  /* ---- matching --------------------------------------------------------- */

  /* Product titles here are full of punctuation that shoppers do not type:
     "Plantronics CS540+Polycom EHS ... (APP-51)". Both sides of the comparison
     get flattened the same way, so "app 51" and "cs540 polycom" both land. */
  function norm(s) {
    return String(s || '')
      .toLowerCase()
      .replace(/[+/(),._\-–—:;|]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function tokens(q) {
    var n = norm(q);
    return n ? n.split(' ') : [];
  }

  /* AND, not OR. Every token must appear somewhere in the haystack — with OR,
     "wireless poly" would return every wireless headset on the page. */
  function matches(hay, toks) {
    for (var i = 0; i < toks.length; i++) {
      if (hay.indexOf(toks[i]) === -1) return false;
    }
    return true;
  }

  /* ---- layer 1: the cards already on the page --------------------------- */

  function applyLocal(toks) {
    var list = cards();
    var shown = 0;
    var onPage = {};

    list.forEach(function (card) {
      var hay = card.getAttribute('data-hp-search') || '';
      var hit = toks.length === 0 || matches(hay, toks);
      /* toggle a class, never remove the node: hsp-ga4-events.js watches
         document.body with a childList MutationObserver, and removing cards
         would fire a spurious view_item_list on every keystroke. */
      card.classList.toggle(NOMATCH, !hit);
      if (hit) {
        shown++;
        var h = card.getAttribute('data-hp-handle');
        if (h) onPage[h] = true;
      }
    });

    return { total: list.length, shown: shown, onPage: onPage };
  }

  /* ---- layer 2: the JSON index ------------------------------------------ */

  function cacheKey(root) {
    return 'hpcs:' + root.getAttribute('data-collection-handle') + ':' + root.getAttribute('data-total');
  }

  function readCache(root) {
    try {
      var raw = sessionStorage.getItem(cacheKey(root));
      return raw ? JSON.parse(raw) : null;
    } catch (e) { return null; }
  }

  function writeCache(root, data) {
    try { sessionStorage.setItem(cacheKey(root), JSON.stringify(data)); } catch (e) { /* quota — fine */ }
  }

  function fetchPage(base, page) {
    var url = base + (base.indexOf('?') === -1 ? '?' : '&') + 'view=searchdata&page=' + page;
    return fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.text();
      })
      .then(function (t) { return JSON.parse(t.trim()); });
  }

  /* Resolves to an array (possibly empty). Never rejects — an index failure
     must degrade to page-scoped search, not to a broken search box. */
  function ensureIndex(root) {
    if (index)        return Promise.resolve(index);
    if (indexFailed)  return Promise.resolve([]);
    if (indexPending) return indexPending;

    var pages = parseInt(root.getAttribute('data-pages'), 10) || 1;
    var total = parseInt(root.getAttribute('data-total'), 10) || 0;
    if (pages <= 1 || total <= 0) { index = []; return Promise.resolve(index); }

    var cached = readCache(root);
    if (cached) { index = cached; return Promise.resolve(index); }

    /* The collection page paginates by 24; this template paginates by 50. */
    var need = Math.min(Math.ceil(total / 50), MAX_PAGES);
    var base = root.getAttribute('data-collection-url') || window.location.pathname;
    var jobs = [];
    for (var p = 1; p <= need; p++) jobs.push(fetchPage(base, p));

    indexPending = Promise.all(jobs)
      .then(function (chunks) {
        index = chunks.reduce(function (acc, c) { return acc.concat(c); }, []);
        writeCache(root, index);
        indexPending = false;
        return index;
      })
      .catch(function () {
        indexFailed  = true;
        indexPending = false;
        return [];
      });

    return indexPending;
  }

  function liteCard(p) {
    var a = document.createElement('a');
    a.className = 'hp-product-card hp-product-card--lite';
    a.href = p.u;

    var media = document.createElement('div');
    media.className = 'hp-card__media';
    if (p.i) {
      var img = document.createElement('img');
      img.src = p.i;
      img.alt = p.t;
      img.loading = 'lazy';
      img.decoding = 'async';
      media.appendChild(img);
    }
    a.appendChild(media);

    var info = document.createElement('div');
    info.className = 'hp-card__info';

    var h = document.createElement('h3');
    h.className = 'hp-card__title';
    h.textContent = p.t;
    info.appendChild(h);

    var price = document.createElement('div');
    price.className = 'hp-card__price';
    var now = document.createElement('span');
    now.className = 'hp-card__price-now';
    now.textContent = p.p;
    price.appendChild(now);
    if (p.c) {
      var was = document.createElement('s');
      was.className = 'hp-card__price-was';
      was.textContent = p.c;
      price.appendChild(was);
    }
    info.appendChild(price);

    var cta = document.createElement('span');
    cta.className = 'hp-card__cta';
    cta.textContent = 'View product →';
    info.appendChild(cta);

    a.appendChild(info);
    return a;
  }

  function renderMore(items) {
    var box  = document.querySelector('[data-hp-colsearch-more]');
    var gridEl = document.querySelector('[data-hp-colsearch-more-grid]');
    var title  = document.querySelector('[data-hp-colsearch-more-title]');
    if (!box || !gridEl) return 0;

    gridEl.textContent = '';
    if (!items.length) { box.hidden = true; return 0; }

    /* Capped for DOM sanity — a two-character query on a 420-product
       collection can match hundreds. The status line reports the real total. */
    var slice = items.slice(0, 24);
    slice.forEach(function (p) { gridEl.appendChild(liteCard(p)); });

    if (title) {
      title.textContent = items.length > slice.length
        ? 'Showing ' + slice.length + ' of ' + items.length + ' more matches elsewhere in this collection'
        : (items.length + (items.length === 1 ? ' more match' : ' more matches') + ' elsewhere in this collection');
    }
    box.hidden = false;
    return slice.length;
  }

  /* ---- painting --------------------------------------------------------- */

  function setEscapeHrefs(q) {
    document.querySelectorAll('[data-hp-colsearch-escape]').forEach(function (a) {
      var tpl = a.getAttribute('data-url-template');
      if (tpl) a.href = tpl.replace('__HPQ__', encodeURIComponent(q));
    });
    document.querySelectorAll('[data-hp-colsearch-empty-term]').forEach(function (el) {
      el.textContent = q;
    });
  }

  function setStatus(html, loading) {
    roots().forEach(function (root) {
      var el = root.querySelector('.hp-colsearch__status');
      if (!el) return;
      if (!html) { el.hidden = true; el.textContent = ''; return; }
      el.innerHTML = html;
      el.classList.toggle('is-loading', !!loading);
      el.hidden = false;
    });
  }

  function setClearVisible(on) {
    document.querySelectorAll('[data-hp-colsearch-clear]').forEach(function (b) {
      if (b.closest('.hp-colsearch')) b.hidden = !on;
    });
  }

  /* Pagination is about the unfiltered collection; leaving it up during a
     search invites a click that silently discards the query. */
  function setChromeHidden(on) {
    var nav = document.querySelector('.hp-pagination');
    if (nav) nav.style.display = on ? 'none' : '';
    var count = document.querySelector('.hp-results__count');
    if (count) count.style.visibility = on ? 'hidden' : '';
  }

  /* Sends through assets/hsp-ga4-events.js so this inherits its gtag queue and
     retry. It used to push to window.dataLayer, and there is no GTM container on
     this site, so every in-collection search since this shipped was discarded.
     Deliberately NOT named `search` — the Google & YouTube app pixel owns that
     for site-wide search, and reusing the name would blend two different
     behaviours into one metric. */
  function trackGA(q, shown) {
    if (!window.HSPGA4 || q === gaSent || q.length < 2) return;
    gaSent = q;
    var root = first();
    window.HSPGA4.send('collection_search', {
      search_term: q,
      results_count: shown,
      index_used: !!(index && index.length),
      collection_handle: root ? root.getAttribute('data-collection-handle') : ''
    });
  }

  /* ---- the main pass ---------------------------------------------------- */

  function run() {
    var root = first();
    if (!root) return;

    var q    = query.trim();
    var toks = tokens(q);
    var res  = applyLocal(toks);

    setClearVisible(q !== '');
    setEscapeHrefs(q);
    setChromeHidden(q !== '');

    var empty = document.querySelector('[data-hp-colsearch-empty]');
    var more  = document.querySelector('[data-hp-colsearch-more]');

    if (q === '') {
      if (empty) empty.hidden = true;
      if (more)  more.hidden = true;
      var g0 = grid(); if (g0) g0.hidden = false;
      setStatus('');
      gaSent = '';
      return;
    }

    var g = grid();
    if (g) g.hidden = res.shown === 0;

    var pages = parseInt(root.getAttribute('data-pages'), 10) || 1;
    var total = parseInt(root.getAttribute('data-total'), 10) || res.total;

    /* Single-page collection: what is on screen IS the collection, so the
       answer is complete and there is nothing to fetch. */
    if (pages <= 1) {
      if (more) more.hidden = true;
      if (empty) empty.hidden = res.shown !== 0;
      setStatus(res.shown === 0
        ? 'No matches'
        : '<b>' + res.shown + '</b> of ' + res.total + ' shown');
      clearTimeout(gaTimer);
      gaTimer = setTimeout(function () { trackGA(q, res.shown); }, GA_IDLE);
      return;
    }

    /* Multi-page, but too short to be worth pulling ~9 requests for: one or two
       characters match most of the catalogue anyway. Stay page-scoped and say
       so, rather than quietly implying this is the whole collection. */
    if (q.length < MIN_INDEX_CHARS) {
      if (more) more.hidden = true;
      if (empty) empty.hidden = res.shown !== 0;
      setStatus('<b>' + res.shown + '</b> on this page · keep typing to search the whole collection');
      return;
    }

    /* Multi-page: report the page result immediately, then extend.
       Deliberately quotes no total yet — paginate.items counts products this
       page will not render (the discontinued skip in the grid loop runs after
       Shopify has paginated), so it overstates the collection badly. The index
       length is the only trustworthy denominator and it is not loaded yet. */
    setStatus('<b>' + res.shown + '</b> on this page · searching the whole collection…', true);

    ensureIndex(root).then(function (idx) {
      /* A slower fetch must not overwrite a newer query's output. */
      if (query.trim() !== q) return;

      if (!idx.length) {
        if (more) more.hidden = true;
        if (empty) empty.hidden = res.shown !== 0;
        setStatus(res.shown === 0
          ? 'No matches on this page'
          : '<b>' + res.shown + '</b> on this page');
        return;
      }

      var off = idx.filter(function (p) {
        if (res.onPage[p.h]) return false;
        var hay = norm([p.t, p.v, p.y, p.h, p.g].join(' '));
        return matches(hay, toks);
      });

      var extra = renderMore(off);
      var all   = res.shown + off.length;
      /* idx.length, not paginate.items: the index is the set actually searched,
         with discontinued products already stripped. */
      var searched = idx.length;

      if (empty) empty.hidden = all !== 0;
      setStatus(all === 0
        ? 'No matches in any of the ' + searched + ' products here'
        : '<b>' + all + '</b> of ' + searched + ' products' +
          (off.length ? ' · ' + res.shown + ' on this page' : ''));

      clearTimeout(gaTimer);
      gaTimer = setTimeout(function () { trackGA(q, all); }, GA_IDLE);
      void extra;
    });
  }

  function setQuery(value, source) {
    query = value;
    /* Keep the desktop and mobile boxes in step so rotating a phone or
       resizing across 1024px does not appear to wipe the search. */
    document.querySelectorAll(INPUT).forEach(function (i) {
      if (i !== source && i.value !== value) i.value = value;
    });
    clearTimeout(timer);
    timer = setTimeout(run, DEBOUNCE);
  }

  /* ---- wiring ----------------------------------------------------------- */

  document.addEventListener('input', function (e) {
    var box = e.target.closest && e.target.closest(INPUT);
    if (!box) return;
    setQuery(box.value, box);
  });

  /* ---- collapsed-on-touch panel ----------------------------------------
     On touch the mobile box is hidden behind the magnifier in .hp-results__bar.
     The state class goes on [data-hp-collection] rather than on the button
     because hp-filters.js innerHTML-swaps [data-hp-results] — and the button
     lives inside it — on every filter change. syncToggles() re-stamps
     aria-expanded on whatever button exists now. */
  function panelHost() { return document.querySelector('[data-hp-collection]'); }

  function syncToggles(open) {
    document.querySelectorAll('[data-hp-colsearch-toggle]').forEach(function (b) {
      b.setAttribute('aria-expanded', open ? 'true' : 'false');
    });
  }

  function setPanel(open, focusInput) {
    var host = panelHost();
    if (!host) return;
    host.classList.toggle('hp-search-open', open);
    syncToggles(open);
    if (open && focusInput) {
      var box = document.querySelector('.hp-colsearch--mobile.is-ready ' + INPUT);
      if (box) box.focus();
    }
  }

  document.addEventListener('click', function (e) {
    var t = e.target.closest && e.target.closest('[data-hp-colsearch-toggle]');
    if (!t) return;
    e.preventDefault();
    var host = panelHost();
    setPanel(!(host && host.classList.contains('hp-search-open')), true);
  });

  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('[data-hp-colsearch-clear]');
    if (!btn) return;
    e.preventDefault();
    document.querySelectorAll(INPUT).forEach(function (i) { i.value = ''; });
    setQuery('', null);
    /* Clearing is the way out of the panel on touch. Leaving it open with an
       empty field just re-costs the row the magnifier was added to save. */
    setPanel(false, false);
    var focusable = document.querySelector('.hp-colsearch.is-ready ' + INPUT);
    if (focusable && focusable.offsetParent !== null) focusable.focus();
  });

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    var box = e.target.closest && e.target.closest(INPUT);
    if (!box || box.value === '') return;
    box.value = '';
    setQuery('', box);
  });

  /* hp-filters.js innerHTML-swaps [data-hp-results] on every filter change, so
     the cards, the empty state and the more-results box are all brand new
     afterwards. Without this the active search would silently stop applying. */
  document.addEventListener('hp:results:rendered', function () {
    /* The results bar — and with it the magnifier — was just rebuilt from the
       server response, so the fresh button carries aria-expanded="false" no
       matter what the panel is actually doing. Re-stamp it from the host class,
       which survived the swap. */
    var host = panelHost();
    syncToggles(!!(host && host.classList.contains('hp-search-open')));
    if (query.trim() !== '') run();
  });

  function init() {
    var found = roots();
    if (!found.length) return;
    /* Reveal only now — a box that cannot work without JS should not be
       painted for a visitor who has none. */
    found.forEach(function (r) { r.classList.add('is-ready'); });

    /* Tells the stylesheet it is safe to collapse the mobile box behind the
       magnifier. Without it the box stays visible, so a visitor whose JS failed
       gets the old always-on row rather than a search they cannot open. */
    document.documentElement.classList.add('hp-colsearch-js');

    /* A restored value after a back-navigation would otherwise leave products
       filtered by a query the shopper cannot see. */
    var restored = document.querySelector(INPUT);
    if (restored && restored.value.trim() !== '') {
      setPanel(true, false);
      setQuery(restored.value, restored);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
